import { eldProviderLabel } from '@/components/Chat/ChatConfirmInformation'
import { EditBusinessDetailsDialog } from '@/components/Chat/EditBusinessDetailsDialog'
import { EditOwnerInfoDialog } from '@/components/Chat/EditOwnerInfoDialog'
import { Button } from '@/components/ui/button'
import { formatPhone } from '@/lib/formatters'
import { formatAddress } from '@/models/address'
import { type Business, yearsInBusinessLabel } from '@/models/business'
import type { InsuranceApplication } from '@/models/insuranceApplication'
import { maritalStatusLabel } from '@/models/user'
import { useSummaryEditor } from './summaryPopover'
import { SummaryBody, SummaryRow } from './SummaryPrimitives'

const ENTITY_TYPE_LABELS: Record<string, string> = {
  individual: 'Individual',
  partnership: 'Partnership',
  llc: 'LLC',
  corporation: 'Corporation',
}

function formatDob(dob: string | null | undefined): string {
  if (!dob) return '-'
  return new Date(`${dob}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

interface BusinessDetailsSummaryProps {
  application: InsuranceApplication
  business: Business | null | undefined
  onSuccess: () => void
  /** Shows the business and owner detail with no way to change it. */
  readonly?: boolean
}

/** Business + owner detail for the summary popover, editable through the existing dialogs. */
export function BusinessDetailsSummary({
  application,
  business,
  onSuccess,
  readonly,
}: BusinessDetailsSummaryProps) {
  const businessEditor = useSummaryEditor()
  const ownerEditor = useSummaryEditor()
  const owner = application.businessOwner

  return (
    <SummaryBody>
      {!readonly && (
        <>
          <EditBusinessDetailsDialog
            open={businessEditor.isOpen}
            onOpenChange={businessEditor.setOpen}
            onSuccess={onSuccess}
            business={business}
            entityType={application.entityType}
            hasEld={application.hasEld}
            eldProvider={application.eldProvider}
            orgId={application.orgId}
          />
          <EditOwnerInfoDialog
            open={ownerEditor.isOpen}
            onOpenChange={ownerEditor.setOpen}
            onSuccess={onSuccess}
            owner={owner ?? undefined}
            orgId={application.orgId}
          />
        </>
      )}

      <SummaryRow label="DOT number" value={business?.dotNumber} />
      <SummaryRow label="Company" value={business?.name} />
      <SummaryRow
        label="Business type"
        value={application.entityType ? (ENTITY_TYPE_LABELS[application.entityType] ?? '-') : '-'}
      />
      <SummaryRow label="Address" value={business?.address ? formatAddress(business.address) : '-'} />
      <SummaryRow label="Years in business" value={yearsInBusinessLabel(business?.yearsInBusiness)} />
      <SummaryRow
        label="ELD provider"
        value={eldProviderLabel(application.hasEld, application.eldProvider)}
      />
      {!readonly && (
        <Button type="button" variant="outline" size="sm" onClick={businessEditor.open}>
          Edit business details
        </Button>
      )}

      <div className="border-ink-100 mt-2 border-t pt-3">
        <SummaryRow label="Owner" value={owner ? `${owner.firstName} ${owner.lastName}`.trim() : '-'} />
        <SummaryRow label="Email" value={owner?.email} />
        <SummaryRow label="Phone number" value={formatPhone(owner?.mobile)} />
        <SummaryRow label="Date of birth" value={formatDob(owner?.dob)} />
        <SummaryRow label="Marital status" value={maritalStatusLabel(owner?.maritalStatus)} />
        {!readonly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 w-full"
            onClick={ownerEditor.open}
          >
            Edit owner info
          </Button>
        )}
      </div>
    </SummaryBody>
  )
}
